<script>
  import dayjs from "dayjs";
  import { getContext } from "svelte";
  import { derived } from "svelte/store";
  import { usePSITableHeaderDataQuery } from "./usePSITableHeaderDataQuery.js";
  import { usePSITableRowsDataQuery } from "./usePSITableRowsDataQuery.js";
  import { useProjectDataQuery } from "../../../hooks/useProjectDataQuery.js";
  import Loading from "../../../components/pages/Loading.svelte";

  /**
   * @type {string}
   */
  export let projectId

  // error handlers
  const {handleFetchErrors} = getContext("fetchErrorHandler")

  const projectDataQuery = useProjectDataQuery(projectId, handleFetchErrors);
  const headerDataQuery = usePSITableHeaderDataQuery(projectId, handleFetchErrors);
  const rowsDataQuery = usePSITableRowsDataQuery(projectId, handleFetchErrors);

  const data = derived([projectDataQuery, headerDataQuery, rowsDataQuery], ([projectDataQuery, headerDataQuery, rowsDataQuery]) => {
    if (!projectDataQuery.data || !headerDataQuery.data || !rowsDataQuery.data)  return null;

    const projectData = projectDataQuery.data
    const headerData = headerDataQuery.data
    const rowsData = rowsDataQuery.data

    // first we need to determine where the "dates" columns start by looking at the header.json (headerData)
    // it is assumed the dates are formatted as "M/d" in the columns
    const datesStartIndex = headerData.findIndex(h => /^[0-9]{1,2}\/[0-9]{1,2}$/.test(h))
    if (datesStartIndex < 0) throw new Error("Dates columns could not be found.")

    // second we need to know the project's basis time and determine the start date's year
    const basisTimeString = projectData.Project_BasisTime
    if (!basisTimeString) throw new Error("Project_BasisTime could not be found.")
    const basisTime = dayjs(basisTimeString).toDate()

    // now we can construct date strings. They are formatted as `yyyy/MM/dd`.
    let currentYear = basisTime.getFullYear()
    const dateStrings = []
    for (let i = datesStartIndex; i < headerData.length; i++) {
      const h = headerData[i]
      // 0-padding
      const [m, d] = h.split("/").map(entry => entry.length === 1 ? "0" + entry : entry);
      const dateStringCandidate = `${currentYear}/${m}/${d}`

      // if the year changes
      if (i !== 0 && dateStringCandidate.localeCompare(dateStrings[i - 1]) > 0) {
        currentYear += 1
        dateStrings.push(`${currentYear}/${m}/${d}`)
      } else {
        dateStrings.push(dateStringCandidate)
      }
    }

    // then construct row objects
    const rows = rowsData.map(rowData => {
      const obj = {}
      for (let i = 0; i < datesStartIndex; i++) {
        obj[headerData[i]] = rowData[i]
      }
      for (let i = datesStartIndex; i < headerData.length; i++) {
        obj[dateStrings[i - datesStartIndex]] = rowData[i]
      }
      return obj
    })
      .filter(row => row["種別"] === "予定")

    const fixedColumns = headerData.slice(0, datesStartIndex)
      .filter(column => column)

    return {Dates: dateStrings, FixedColumns: fixedColumns, rows}
  })
</script>

{#if !$data}
  <Loading />
{:else}
  <slot data={$data} />
{/if}
